import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { applyEdit } from '../lib/planning'
import type {
  BoardCycle,
  IssueComment,
  Plan,
  PlanEdit,
  PlanningCycle,
  PlanningCycleState,
  PlanningCycleSummary,
  WriteBackPreview,
  WriteBackResponse,
} from '../types/planning'

export const cycleKey = (cycle: string) => ['planning-cycle', cycle]
export const planningEditKey = (cycle: string) => ['planning-edit', cycle]
const writeKey = (cycle: string) => ['planning-write', cycle]

/** Admin: the imported cycles, newest first. */
export function usePlanningCycles() {
  return useQuery<PlanningCycleSummary[]>({
    queryKey: ['planning-cycles'],
    queryFn: async () => (await api.get('/planning/cycles')).data.items,
  })
}

/** One cycle's draft and edits. Polls so everyone in the meeting sees each change. */
export function usePlanningCycle(cycle: string | undefined) {
  const qc = useQueryClient()
  return useQuery<PlanningCycle>({
    queryKey: cycleKey(cycle ?? ''),
    queryFn: async () => {
      const { data } = await api.get<PlanningCycle>(`/planning/cycles/${encodeURIComponent(cycle!)}`)
      const cached = qc.getQueryData<PlanningCycle>(cycleKey(cycle!))
      return cached && cached.version > data.version ? cached : data
    },
    enabled: !!cycle,
    refetchInterval: 5_000,
  })
}

/** Admin: import or refresh a draft. The room's edits are kept. */
export function useImportPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (plan: Plan) =>
      (await api.put(`/planning/cycles/${encodeURIComponent(plan.cycle)}`, { plan }))
        .data as PlanningCycle,
    onSuccess: (data) => {
      qc.setQueryData(cycleKey(data.cycle), data)
      qc.invalidateQueries({ queryKey: ['planning-cycles'] })
      qc.invalidateQueries({ queryKey: ['planning-board-cycles'] })
    },
  })
}

/** Re-read the cycle from GitHub now; the room's edits stay. */
export function useRefreshPlan(cycle: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<PlanningCycle>(`/planning/cycles/${encodeURIComponent(cycle)}/refresh`))
        .data,
    onSuccess: (data) => {
      // An edit saved while the refresh ran may already be newer: never roll it
      // back (Copilot on #1559). The next poll brings the refreshed draft.
      qc.setQueryData<PlanningCycle>(cycleKey(data.cycle), (old) =>
        !old || data.version >= old.version ? data : old,
      )
      qc.invalidateQueries({ queryKey: ['planning-cycles'] })
    },
  })
}

/** One edit from the meeting. Shown at once; the server's answer replaces it. */
export function usePlanEdit(cycle: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: planningEditKey(cycle),
    mutationFn: async (edit: PlanEdit) =>
      (await api.post(`/planning/cycles/${encodeURIComponent(cycle)}/edits`, edit))
        .data as PlanningCycleState,
    onMutate: async (edit) => {
      await qc.cancelQueries({ queryKey: cycleKey(cycle) })
      qc.setQueryData<PlanningCycle>(cycleKey(cycle), (old) =>
        old ? { ...old, state: applyEdit(old.state, edit) } : old,
      )
    },
    onSuccess: (data) => {
      // Answers can arrive out of order; an older version never replaces a newer one.
      qc.setQueryData<PlanningCycle>(cycleKey(cycle), (old) =>
        old && data.version >= old.version ? { ...old, ...data } : old,
      )
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: cycleKey(cycle) })
    },
  })
}

export const commentsKey = (cycle: string, issue: number) => ['planning-comments', cycle, issue]

/** Admin: one comment from the meeting, straight onto an issue on the plan. */
export function usePostIssueComment(cycle: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ issue, text }: { issue: number; text: string }) =>
      (
        await api.post<{ url: string }>(
          `/planning/cycles/${encodeURIComponent(cycle)}/issues/${issue}/comments`,
          { text },
        )
      ).data,
    // An open detail panel shows the new comment under the others.
    onSuccess: (_, { issue }) => qc.invalidateQueries({ queryKey: commentsKey(cycle, issue) }),
  })
}

/** Admin: an issue's newest comments, read when its detail panel opens. */
export function useIssueComments(cycle: string, issue: number | undefined) {
  return useQuery({
    queryKey: commentsKey(cycle, issue ?? 0),
    queryFn: async () =>
      (
        await api.get<{ items: IssueComment[] }>(
          `/planning/cycles/${encodeURIComponent(cycle)}/issues/${issue}/comments`,
        )
      ).data.items,
    enabled: !!issue,
    staleTime: 60_000,
  })
}

/** Admin: the cycles on the GitHub project board. Read when the menu opens. */
export function useBoardCycles(enabled: boolean) {
  return useQuery<BoardCycle[]>({
    queryKey: ['planning-board-cycles'],
    queryFn: async () => (await api.get('/planning/github/cycles')).data.items,
    enabled,
    staleTime: 60_000,
  })
}

/** Admin: open a cycle that is not on the page yet, read from the board. The
 *  start date names the board's iteration: the board keeps last year's Q4C1. */
export function useStartCycle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ cycle, start }: { cycle: string; start: string }) =>
      (
        await api.post<PlanningCycle>(
          `/planning/cycles/${encodeURIComponent(cycle)}/refresh`,
          undefined,
          { params: { start } },
        )
      ).data,
    onSuccess: (data) => {
      qc.setQueryData<PlanningCycle>(cycleKey(data.cycle), (old) =>
        !old || data.version >= old.version ? data : old,
      )
      qc.invalidateQueries({ queryKey: ['planning-cycles'] })
      qc.invalidateQueries({ queryKey: ['planning-board-cycles'] })
    },
  })
}

/** Admin: what writing the room's decisions to GitHub would change. Writes nothing. */
export function useWriteBackPreview(cycle: string, enabled: boolean, commentIssue?: number) {
  return useQuery<WriteBackPreview>({
    queryKey: ['planning-writeback', cycle, commentIssue ?? null],
    queryFn: async () =>
      (
        await api.get(`/planning/cycles/${encodeURIComponent(cycle)}/writeback`, {
          params: commentIssue ? { comment_issue: commentIssue } : undefined,
        })
      ).data,
    enabled,
    staleTime: 0,
    // Naming another issue for the read-back re-reads; keep the list meanwhile.
    placeholderData: (previous) => previous,
  })
}

/** Admin: write the ticked changes. GitHub is re-read first; moved-on ones are skipped. */
export function useWriteBack(cycle: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: writeKey(cycle),
    mutationFn: async (body: {
      ids: string[]
      comment_issue?: number
      comment?: string
      version?: number
    }) =>
      (await api.post(`/planning/cycles/${encodeURIComponent(cycle)}/writeback`, body))
        .data as WriteBackResponse,
    // Marked stale, not re-read now: the dialog shows the results meanwhile, and
    // opening it again reads GitHub afresh.
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['planning-writeback', cycle], refetchType: 'none' }),
  })
}

export function usePlanningSaveState(cycle: string) {
  const edits = useMutationState({
    filters: { mutationKey: planningEditKey(cycle), exact: true },
    select: (m) => ({
      id: m.mutationId,
      status: m.state.status,
      edit: m.state.variables as PlanEdit | undefined,
    }),
  })
  const latest = new Map<string, (typeof edits)[number]>()
  for (const e of edits) {
    // A successful replacement of the same field resolves its earlier failure.
    // Add/increment operations are independent; another success cannot undo a failure.
    latest.set(e.edit?.op === 'set' ? JSON.stringify(e.edit.path) : String(e.id), e)
  }
  return {
    pending: edits.filter((e) => e.status === 'pending').length,
    failed: [...latest.values()].filter((e) => e.status === 'error').length,
  }
}

export function useLastPlanningWrite(cycle: string) {
  const attempts = useMutationState({
    filters: { mutationKey: writeKey(cycle), exact: true },
    select: (m) => ({
      status: m.state.status,
      data: m.state.data as WriteBackResponse | undefined,
      error: m.state.error,
    }),
  })
  return attempts[attempts.length - 1]
}
